import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputLabel,
  Pagination,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { ChangeEvent, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import accountApi from '~/apis/account';
import equipmentApi from '~/apis/equipment';
import { CloseIcon, DeleteBin6LineIcon } from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { useAppDispatch } from '~/redux/hooks';
import { ROLE_AGENT } from '~/utils/constants/account';
import { handleApiError, notify } from '~/utils/helper';
import { IAccount } from '~/utils/interface/account';
import { IEquipment, IGetTranferDevicesParams, ITranferItem } from '~/utils/interface/equipment';
import classNames from 'classnames/bind';
import styles from './TransferDeviceDialog.module.scss';
import utilityApi from '~/apis/utility';
import BaseRow from '~/components/common/base-row/BaseRow';
import { IMasterDataItem } from '~/utils/interface/master';
import { commonActions } from '~/thunks/common/commonSlice';

const cx = classNames.bind(styles);

type TranferDeviceDialogProps = {
  open: boolean;
  data?: IAccount;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

const getTransferFormInit: IGetTranferDevicesParams = {
  agent: '',
  page: 1,
  pageSize: 20,
};

const TranferDeviceDialog = (props: TranferDeviceDialogProps) => {
  const { t } = useTranslation(['admin/agent', 'admin/common']);
  const dispatch = useAppDispatch();
  const [agent, setAgent] = useState<IAccount>();
  const [deviceList, setDeviceList] = useState<IEquipment[]>([]);
  const [deviceIds, setDeviceIds] = useState<Array<number>>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [agentOptions, setAgentOptions] = useState<IMasterDataItem[]>([]);
  const [targetAgent, setTargetAgent] = useState<IAccount>();
  const [tranferList, setTranferList] = useState<ITranferItem[]>([]);

  const getTransferForm = useFormik<IGetTranferDevicesParams>({
    initialValues: getTransferFormInit,
    onSubmit: async () => {
      handleGetTranferDevices();
    },
  });

  useEffect(() => {
    if (props.open && props.data) {
      (async () => {
        try {
          const response = await accountApi.getByRoleList(ROLE_AGENT, { username: props.data?.username });
          if (response.data?.data?.length) {
            const data = response.data.data[0];
            setAgent(data);
          }
        } catch (error) {
          handleApiError(error);
        }
      })();

      handleGetTranferDevices();

      (async () => {
        try {
          const response = await utilityApi.getListAgents();
          setAgentOptions(
            response.data.map((item, index) => {
              return {
                id: item.username,
                name: item.username + ' - ' + item.name,
                data0: item.name,
              };
            })
          );
        } catch (error) {
          handleApiError(error);
        }
      })();

      handleGetTransferList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, props.open]);

  useEffect(() => {
    getTransferForm.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTransferForm.values]);

  const getTransferDevices = async (overrideParams = {}) => {
    try {
      return (
        await equipmentApi.getListTranferDevices({
          ...getTransferForm.values,
          agent: String(props.data?.username),
          ...overrideParams,
        })
      )?.data;
    } catch (error) {
      handleApiError(error);
      return { total: 0, data: [] };
    }
  };

  const handleGetTranferDevices = async () => {
    const response = await getTransferDevices();
    setTotalItems(response.total);
    setDeviceList(response.data);
  };

  const handleGetTransferList = async () => {
    try {
      const response = await equipmentApi.getTranferList(String(props.data?.username));
      setTranferList(response.data);
    } catch (error) {
      setTranferList([]);
    }
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleCheckAll = async (_: ChangeEvent<HTMLInputElement> | null, checked: boolean) => {
    if (checked) {
      const res = await getTransferDevices({ page: 1, pageSize: totalItems });
      setDeviceIds(res.data?.map((account: IEquipment) => account.device_id));
    } else {
      setDeviceIds([]);
    }
  };

  const handleCheck = async (row: IEquipment, checked: boolean) => {
    if (checked) {
      setDeviceIds((prev) => [...(prev ?? []), ...[row.device_id]]);
    } else {
      setDeviceIds(deviceIds.filter((deviceId) => deviceId !== row.device_id));
    }
  };

  const handleAddTranferList = async () => {
    if (!targetAgent) {
      handleApiError(t('tranferDeviceDialog.targetAgentRequired'));
      return;
    }
    dispatch(commonActions.setIsLoading(true));
    try {
      await equipmentApi.addTranferList({
        device_ids: deviceIds,
        current_agent: String(props.data?.username),
        target_agent: String(targetAgent),
      });
      notify('success', t('tranferDeviceDialog.tranferDevicesSuccess'));
      setDeviceIds([]);
      handleGetTransferList();
      handleGetTranferDevices();
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch(commonActions.setIsLoading(false));
    }
  };

  const handleDeleteTranferList = async (id: string) => {
    dispatch(commonActions.setIsLoading(true));
    try {
      await equipmentApi.deleteTranferList(id);
      notify('success', t('tranferDeviceDialog.deleteSuccess'));
      handleGetTransferList();
      handleGetTranferDevices();
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch(commonActions.setIsLoading(false));
    }
  };

  const handleConfirmTranferList = async () => {
    dispatch(commonActions.setIsLoading(true));
    try {
      for (let transfer of tranferList) {
        await equipmentApi.confirmTranferList(transfer.id);
      }

      notify('success', t('success', { ns: 'admin/common' }));
      handleSetOpen(false, true);
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch(commonActions.setIsLoading(false));
    }
  };

  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='xl'>
        <DialogTitle className='bg-[#ED1002]'>
          {t('tranferDeviceDialog.title')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography className='text-[#005F73] mb-[10px] font-bold'>{t('tranferDeviceDialog.agentInfo')}</Typography>
          <Box className='flex flex-row gap-[53px] mb-[30px]'>
            <Box>
              <Box className='flex flex-row gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('detailAgentDialog.name')}:</Box>
                <Box>{agent?.name}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('detailAgentDialog.address')}:</Box>
                <Box>{agent?.address}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('detailAgentDialog.representative')}:</Box>
                <Box>{agent?.representative}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('detailAgentDialog.phone')}:</Box>
                <Box>{agent?.phone}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('detailAgentDialog.email')}:</Box>
                <Box>{agent?.email}</Box>
              </Box>
            </Box>
            <Box>
              <Box className='flex flex-row gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('detailAgentDialog.distributor')}:</Box>
                <Box>{agent?.distributor}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('detailAgentDialog.username')}:</Box>
                <Box>{agent?.username}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('detailAgentDialog.contractId')}:</Box>
                <Box>{agent?.contract_id}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('detailAgentDialog.createdAt')}:</Box>
                <Box>{agent?.created_at ? dayjs(agent.created_at).format('DD/MM/YYYY') : ''}</Box>
              </Box>
            </Box>
          </Box>
          <Box className='border border-[#005F73] text-[#005F73] rounded-[10px] p-[10px] mb-[20px]'>
            <Box className='border-b border-[#D9D9D9]'>
              <FormControlLabel
                label={t('tranferDeviceDialog.deviceList')}
                sx={{
                  color: 'primary.main',
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 'bold',
                    fontSize: '16px',
                  },
                }}
                control={
                  <Checkbox
                    onChange={handleCheckAll}
                    sx={{
                      color: '#005F73',
                      '&.Mui-checked': {
                        color: '#005F73',
                      },
                    }}
                  />
                }
              />
            </Box>
            <Box className='flex flex-wrap border-b border-[#D9D9D9]'>
              {deviceList.map((device: IEquipment, index: number) => {
                return (
                  <FormControlLabel
                    key={index}
                    label={device.device_code}
                    className='w-1/5 truncate m-0'
                    control={
                      <Checkbox
                        id={device.device_code}
                        checked={(deviceIds ?? []).find((deviceId) => deviceId === device.device_id) ? true : false}
                        onChange={(_, checked) => handleCheck && handleCheck(device, checked)}
                        sx={{
                          color: '#005F73',
                          '&.Mui-checked': {
                            color: '#005F73',
                          },
                        }}
                      />
                    }
                  />
                );
              })}
            </Box>
            <Box className={cx('flex items-center justify-end gap-10 mt-5', 'pagination-box')}>
              <Typography>
                {t('sum')}: <span className='font-bold text-[#005f73]'>{totalItems}</span>{' '}
                {t('tranferDeviceDialog.record')}
              </Typography>
              <Box className='w-[150px]'>
                <BaseSelect
                  options={[
                    { id: 10, name: '10' },
                    { id: 20, name: '20' },
                    { id: 50, name: '50' },
                    { id: 100, name: '100' },
                    { id: 250, name: '250' },
                    { id: 500, name: '500' },
                  ]}
                  value={getTransferForm.values.pageSize}
                  onChange={(e) => {
                    getTransferForm.setFieldValue('pageSize', e.target.value);
                    getTransferForm.setFieldValue('page', 1);
                  }}
                  renderSelected={(option: any) => (
                    <Typography className='px-2'>
                      {option.name}/{t('page').toLowerCase()}
                    </Typography>
                  )}
                />
              </Box>
              <Pagination
                count={Math.max(1, Math.ceil(totalItems / (getTransferForm.values.pageSize ?? 1)))}
                variant='outlined'
                page={getTransferForm.values.page ?? 1}
                onChange={(e, value: number) => {
                  getTransferForm.setFieldValue('page', value);
                }}
              />
              <Box className='flex items-center gap-3'>
                <Typography>{t('page')}</Typography>
                <Box className='rounded-[20px] border border-[#ED1002] text-center py-2 px-7'>
                  {getTransferForm.values.page}
                </Box>
              </Box>
            </Box>
          </Box>

          <BaseRow className='gap-[20px] mb-[20px] !items-center'>
            <InputLabel>{t('tranferDeviceDialog.selectAgent')}</InputLabel>
            <Box className='w-[250px]'>
              <BaseSelect
                size='medium'
                options={agentOptions}
                value={targetAgent}
                placeholder={t('tranferDeviceDialog.selectAgentPlaceholder')}
                isSearchEnable
                onChange={(e) => setTargetAgent(e.target.value)}
                displayInvalidValue
              />
            </Box>
            <Box>
              <Button
                size='medium'
                variant='contained'
                color='primary'
                className='min-w-[125px] flex ml-auto'
                onClick={handleAddTranferList}
              >
                {t('tranferDeviceDialog.tranferDevices')}
              </Button>
            </Box>
          </BaseRow>
          {tranferList?.length ? (
            <Box className='rounded-[8px] border border-[#CBC6C6] p-[8px] w-[500px] flex flex-col'>
              {tranferList.map((item: ITranferItem, index: number) => {
                return (
                  <Box key={index} className='flex justify-between items-center'>
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: t('tranferDeviceDialog.tranferListContent', {
                          device: item?.device_ids?.length,
                          targetAgentName: item.target_agent_name,
                        }),
                      }}
                    />
                    <IconButton onClick={() => handleDeleteTranferList(item.id)}>
                      <DeleteBin6LineIcon fill='#ED1002' />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='secondary' className='min-w-[125px]' onClick={() => handleSetOpen(false)}>
            {t('cancel', { ns: 'admin/common' })}
          </Button>
          <Button
            disabled={!tranferList?.length}
            variant='contained'
            color='secondary'
            className='min-w-[125px] flex ml-auto'
            onClick={() => handleConfirmTranferList()}
          >
            {t('save', { ns: 'admin/common' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default memo(TranferDeviceDialog);
